import { getAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { molecule } from 'bunshi';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { DailyRoomNameScope } from './scopes';

/**
 * Appointment Molecule
 *
 * Provides appointment atoms scoped to a dailyRoomName (via DailyRoomNameScope).
 * Shared by sessionMolecule and mealPlanMolecule so both can access appointment
 * data without duplicating the fetch.
 *
 * Includes:
 * - Query atom for appointment data
 * - Derived atoms for commonly needed IDs
 */
export const appointmentMolecule = molecule((_, scope) => {
  const dailyRoomName = scope(DailyRoomNameScope);

  const dailyRoomNameAtom = atom(() => dailyRoomName);

  const appointmentQueryAtom = atomWithQuery(() => ({
    queryKey: ['berrystreetGetAppointmentByRoomId', dailyRoomName],
    queryFn: (): Promise<AppointmentResponse> =>
      getAppointmentByRoomId(dailyRoomName),
    enabled: !!dailyRoomName,
    retry: 1,
  }));

  const appointmentAtom = atom((get) => {
    const queryResult = get(appointmentQueryAtom);
    return queryResult.data;
  });

  const patientIdAtom = atom((get) => {
    return get(appointmentAtom)?.client_id?.toString();
  });

  const providerIdAtom = atom((get) => {
    return get(appointmentAtom)?.provider_id?.toString();
  });

  const healthieClientIdAtom = atom((get) => {
    return get(appointmentAtom)?.client_id;
  });

  const healthieProviderIdAtom = atom((get) => {
    return get(appointmentAtom)?.provider_id;
  });

  const healthieAppointmentAtom = atom((get) => {
    return get(appointmentAtom)?.healthie;
  });

  const healthieAppointmentIdAtom = atom((get) => {
    return get(appointmentAtom)?.healthie_id?.toString();
  });

  return {
    dailyRoomNameAtom,
    appointmentQueryAtom,
    appointmentAtom,
    patientIdAtom,
    providerIdAtom,
    healthieClientIdAtom,
    healthieProviderIdAtom,
    healthieAppointmentAtom,
    healthieAppointmentIdAtom,
  };
});
